/** @jsx jsx */
// eslint-disable-next-line jira/restricted/react-component-props
import React, { Component, forwardRef, type ComponentProps } from 'react';
import { cssMap, cx, jsx } from '@compiled/react';
import debounce from 'lodash/debounce';
import flatMap from 'lodash/flatMap';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Field } from '@atlaskit/form';
import { CreatableSelect, components, type OptionProps, type StylesConfig } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import { Flex } from '@atlaskit/primitives';
import type { PopupComponentProps, TriggerProps } from '@atlaskit/popup';
import { layers as themeLayers } from '@atlaskit/theme/constants';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { JiraInlineDialog as InlineDialog } from '@atlassian/jira-inline-dialog/src/ui/jira-inline-dialog.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { memoizeGetAutoGeneratedColorForLabels } from '@atlassian/jira-option-color-picker/src/option-color-generator/index.tsx';
import { OptionTag } from '@atlassian/jira-option-color-picker/src/option-tag/index.tsx';
import { getColorMetadata } from '@atlassian/jira-option-color-picker/src/color-picker/utils/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { LabelsColorPickerForm } from '@atlassian/jira-option-color-picker/src/labels-color-picker/index.tsx';
import type { ColorOptionData } from '@atlassian/jira-option-color-picker/src/labels-color-picker/types.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import messages from './messages.tsx';
import type { LabelOption } from './model/index.tsx';
import { labelsService, validationErrorMessage } from './services/index.tsx';

const DEBOUNCE_TIME = 500;

export type Props = {
	isRequired: boolean;
	/* This will show a red border and warning icon when true. This won't render any validation message as consumers are
	expected to render that themselves outside of the component. Note this does not affect the internal validation state
	of the component. e.g. if `isInvalid=false` and the picker failed to fetch options, then an error state will still
	be shown. */
	isInvalid: boolean;
} & JSX.LibraryManagedAttributes<typeof CreatableSelect, ComponentProps<typeof CreatableSelect>> & {
		label: string;
		value?: ReadonlyArray<string>;
		noPortal: boolean;
		baseUrl: string;
		customFieldId?: string;
		/** Focus the control when it is mounted */
		autoFocus?: boolean;
		initialOptions?: LabelOption[];
		enableAnimation?: boolean;
		onChange: (arg1: string[], arg2?: UIAnalyticsEvent) => void;
		noOptionsErrorMessage?: string;
		isDefaultValidationState?: boolean;
		intl: IntlShape;
		isColored?: boolean;
	};

export type State = {
	labels: string[];
	options: LabelOption[];
	errorType: 'validation' | 'general' | undefined;
	isLoading: boolean;
	showColorPickerPopup: boolean;
	selectedLabelForEdit: LabelOption | undefined;
};

// This functions double checks for labels with spaces or commas.
// If such is detected then the string is split into two separate labels.
// f.in. ["foo bar", "baz"] => ["foo", "bar", "baz"]
export const transformLabels = (selectedOptions: string[]): string[] =>
	flatMap(selectedOptions, (str: string) => str.trim().split(/[\s,]+/)).filter(
		(label) => label.length > 0,
	);

const multiValueRemoveStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > span > svg': {
			transform: 'scale(1.5)',
			fill: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > span > svg > g > path': {
			d: 'path("m 2 10 l 4 -4 m 4 -4 L 6 6 m 0 0 L 2 2 m 4 4 l 4 4")',
			transform: 'translate(5px, 6px)',
			stroke: token('color.text'),
			strokeWidth: '1.5',
		},
	},
});

const multiValueStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > div > div': {
			paddingLeft: token('space.0'),
			paddingRight: token('space.0'),
		},
	},
	isVisualRefreshDisabled: {
		borderRadius: token('border.radius.100'),
		backgroundColor: 'transparent',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div': {
			paddingLeft: token('space.050'),
			paddingRight: token('space.0'),
			paddingTop: token('space.0'),
			paddingBottom: token('space.0'),
		},
	},
});

const multiValueLabelStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > div': {
			borderColor: 'transparent',
		},
	},
});

// eslint-disable-next-line jira/react/no-class-components
export class LabelsPicker extends Component<Props, State> {
	static defaultProps = {
		isInvalid: false,
		noPortal: false,
		autoFocus: false,
	};

	state = {
		labels: [],
		options: [],
		errorType: undefined,
		isLoading: false,
		showColorPickerPopup: false,
		selectedLabelForEdit: undefined,
	};

	componentDidMount() {
		this.setState({ isLoading: true });
		this.fetchOptions();
	}

	componentDidUpdate(prevProps: Props) {
		// automatically refetch options when these props change
		if (
			prevProps.baseUrl !== this.props.baseUrl ||
			prevProps.customFieldId !== this.props.customFieldId
		) {
			this.setState({ isLoading: true });
			this.fetchOptions();
		}
	}

	loadingMessage = () => this.props.intl.formatMessage(messages.loading);

	noOptionsMessage = () => {
		const { errorType } = this.state;
		const {
			intl: { formatMessage },
			noOptionsErrorMessage,
		} = this.props;
		const message =
			errorType === 'general' && noOptionsErrorMessage
				? noOptionsErrorMessage
				: formatMessage(messages.noOptions);

		return message;
	};

	onSelected = (
		selection: LabelOption[] | LabelOption | null,
		actionMeta: {
			action: string;
		},
		analyticsEvent?: UIAnalyticsEvent,
	) => {
		if (Array.isArray(selection)) {
			this.onChange(selection, analyticsEvent);
		} else {
			selection && selection.value
				? this.onChange([selection], analyticsEvent)
				: this.onChange([], analyticsEvent);
		}
	};

	onChange(selectedOption: LabelOption[], analyticsEvent?: UIAnalyticsEvent) {
		const labels = selectedOption.map((option) => option.label);
		this.setState({
			labels,
		});
		this.props.onChange(transformLabels(labels), analyticsEvent);
	}

	onCreateOption = (value: string) => {
		const labels = this.props.value || this.state.labels;
		const newLabels = [...labels, value];
		this.setState({
			labels: newLabels,
		});
		this.props.onChange(transformLabels(newLabels));
	};

	debouncedLoadOptions = debounce((filter: string) => {
		this.fetchOptions(filter);
	}, DEBOUNCE_TIME);

	fetchOptions(search = '') {
		labelsService(this.props.baseUrl, search, this.props.customFieldId)
			.then((result: LabelOption[]) => {
				this.setState({
					isLoading: false,
					options: [...(this.props.initialOptions || []), ...result],
				});
				return result;
			})
			.catch((err) => {
				if (err.message === validationErrorMessage) {
					this.setState({
						errorType: 'validation',
						isLoading: false,
						options: [],
					});
				} else {
					this.setState({
						errorType: 'general',
						isLoading: false,
						options: [],
					});
				}
			});
	}

	formatCreateLabel = (value: string) => {
		const {
			intl: { formatMessage },
		} = this.props;
		return `${value} (${formatMessage(messages.new)})`;
	};

	onInputChange = (value: string) => {
		this.setState({ isLoading: true, errorType: undefined });
		this.debouncedLoadOptions(value);
	};

	renderInlineEditButton(option: LabelOption) {
		const handleOnClickEditButton = (event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			this.onEditOption?.(option);
		};

		if (
			option.label &&
			expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			)
		)
			return (
				<IconButton
					testId={`jira-label-picker.inline-edit-button-${option.label}`}
					appearance="subtle"
					icon={EditIcon}
					spacing="compact"
					onClick={handleOnClickEditButton}
					label=""
				/>
			);

		return null;
	}

	formatOptionLabel = (option: LabelOption) => {
		const {
			intl: { formatMessage },
		} = this.props;

		const optionLabel: string = option.label;
		const createNewLabelString = `(${formatMessage(messages.new)})`;

		if (typeof optionLabel === 'string' && optionLabel.endsWith(createNewLabelString)) {
			const optionLabelWithoutNewLabel = optionLabel.slice(0, -createNewLabelString.length).trim();

			return (
				<OptionTag
					name={optionLabel}
					color={memoizeGetAutoGeneratedColorForLabels(optionLabelWithoutNewLabel)}
				/>
			);
		}

		return (
			<OptionTag name={optionLabel} color={memoizeGetAutoGeneratedColorForLabels(optionLabel)} />
		);
	};

	onEditOption(option: LabelOption) {
		this.setState({ selectedLabelForEdit: option, showColorPickerPopup: true });
	}

	resetLabelsColorPickerState = () => {
		this.setState({ selectedLabelForEdit: undefined, showColorPickerPopup: false });
	};

	submitLabelsUpdatedColor(param: ColorOptionData) {
		const selectedLabelForEdit: LabelOption | undefined = this.state.selectedLabelForEdit;
		if (!selectedLabelForEdit) {
			return;
		}
		if (selectedLabelForEdit) {
			return {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				...(selectedLabelForEdit as LabelOption),
				...{
					label: param.name,
					// color: param.color,
					// this will be updated once backend PRs are merged and we can send data to backend
				},
			};
		}
	}

	render() {
		const {
			label,
			isRequired,
			value,
			noPortal,
			intl: { formatMessage },
			isInvalid,
			isColored,
			isDefaultValidationState,
			...rest
		} = this.props;

		const { labels, errorType, options, isLoading, showColorPickerPopup } = this.state;
		const selectedLabelForEdit: LabelOption | undefined = this.state.selectedLabelForEdit;

		const pickerValues: LabelOption[] = (value || labels).map((lab) => ({
			label: lab,
			value: lab,
		}));

		const isInlineDialogOpen = this.props.noOptionsErrorMessage
			? Boolean(errorType === 'validation')
			: Boolean(errorType);

		const validationState =
			!isDefaultValidationState && (isInvalid || errorType) ? 'error' : 'default';

		const selectStyles: StylesConfig<LabelOption, true> = {
			menuPortal: (base) => ({ ...base, zIndex: layers.modal }),
		};

		const labelsValueStyles: StylesConfig<LabelOption, true> = {
			multiValue: (base, { data }) => {
				const { backgroundColor } = getColorMetadata(
					memoizeGetAutoGeneratedColorForLabels(data.label),
				);
				return {
					...base,
					border: `1px solid ${backgroundColor}`,
					'& > div > div > div': {
						paddingLeft: 0,
						paddingRight: 0,
					},
					...(!isVisualRefreshEnabled()
						? {
								borderRadius: token('border.radius.100'),
								backgroundColor: 'transparent',
								'& > div': {
									paddingLeft: token('space.050'),
									paddingRight: 0,
									paddingTop: 0,
									paddingBottom: 0,
								},
							}
						: {}),
				};
			},
			multiValueLabel: (base) => ({
				...base,
				'& > div > div': {
					borderColor: 'transparent',
				},
			}),
		};
		const isVisualRefreshDisabled = !isVisualRefreshEnabled();

		const labelsRemoveIconStyles: StylesConfig<LabelOption, true> = {
			multiValueRemove: (base) => ({
				...base,
				'& > div > span > svg': {
					transform: 'scale(1.5)',
					fill: 'none',
				},
				'& > div > span > svg > g > path': {
					d: 'path("m 2 10 l 4 -4 m 4 -4 L 6 6 m 0 0 L 2 2 m 4 4 l 4 4")',
					transform: 'translate(5px, 6px)',
					stroke: token('color.text'),
					strokeWidth: '1.5',
				},
			}),
		};

		const styles = () => {
			if (isColored) {
				if (!isVisualRefreshEnabled()) {
					return { ...selectStyles, ...labelsValueStyles, ...labelsRemoveIconStyles };
				}
				return { ...selectStyles, ...labelsValueStyles };
			}

			return selectStyles;
		};

		const handleCancel = () => {
			this.resetLabelsColorPickerState();
		};

		const handleSubmit = (param: ColorOptionData) => {
			this.resetLabelsColorPickerState();
			this.submitLabelsUpdatedColor(param);
		};

		const popupContent = () => (
			<LabelsColorPickerForm
				onCancel={handleCancel}
				onSubmit={handleSubmit}
				initialData={{
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					name: selectedLabelForEdit ? (selectedLabelForEdit as LabelOption).label?.toString() : '',
					color: '',
				}}
			/>
		);

		const labelsField = (
			<Field name="platform-ui-pickers-label" label={label} isRequired={isRequired}>
				{({ fieldProps }) => (
					<>
						<InlineDialog
							content={
								errorType === 'validation'
									? formatMessage(messages.validationError)
									: formatMessage(messages.fetchError)
							}
							isOpen={isInlineDialogOpen}
							placement="right"
							messageId="label-picker.inline-dialog"
							messageType="transactional"
						>
							<CreatableSelect
								{...rest}
								inputId={fieldProps.id}
								isClearable={false}
								defaultOptions
								autoFocus={!!this.props.autoFocus}
								// @ts-expect-error - TS2322 - Type '(selection: LabelOption[] | LabelOption | null, actionMeta: {    action: string;}, analyticsEvent?: any) => void' is not assignable to type '(value: unknown, action: ActionMeta<unknown>) => void'.
								onChange={this.onSelected}
								noOptionsMessage={this.noOptionsMessage}
								loadingMessage={this.loadingMessage}
								validationState={validationState}
								// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
								menuPortalTarget={noPortal ? null : document.body}
								value={pickerValues}
								onCreateOption={this.onCreateOption}
								formatCreateLabel={this.formatCreateLabel}
								{...(isColored ? { formatOptionLabel: this.formatOptionLabel } : {})}
								allowCreateWhileLoading
								// @ts-expect-error error TS2322: Type &#39;true&#39; is not assignable to type &#39;false&#39;.
								isMulti
								options={options}
								onInputChange={this.onInputChange}
								isLoading={isLoading}
								// @ts-expect-error - ts2322 -  Type 'ClearIndicatorProps<unknown, boolean, GroupBase<unknown>>' is not assignable to type 'ClearIndicatorProps<LabelOption, true, GroupBase<LabelOption>>'.
								styles={styles()}
								{...(isColored && {
									components: {
										MultiValueContainer: (props) => (
											<components.MultiValueContainer
												{...props}
												xcss={cx(
													multiValueStyles.root,
													isVisualRefreshDisabled && multiValueStyles.isVisualRefreshDisabled,
												)}
											/>
										),
										MultiValueLabel: (props) => (
											<components.MultiValueLabel {...props} xcss={multiValueLabelStyles.root} />
										),
										MultiValueRemove: (props) => (
											<components.MultiValueRemove
												{...props}
												xcss={isVisualRefreshDisabled && multiValueRemoveStyles.root}
											/>
										),
										Option: (optionProps: OptionProps<LabelOption>) => {
											if (
												expVal(
													'adding-color-to-labels-field-experiment-milestone2',
													'isColorfulNewLabelsEnabled',
													false,
												)
											) {
												return (
													<components.Option {...optionProps}>
														<Flex justifyContent="space-between">
															{this.formatOptionLabel(optionProps.data)}
															{this.renderInlineEditButton(optionProps.data)}
														</Flex>
													</components.Option>
												);
											}
											return <components.Option {...optionProps} />;
										},
									},
								})}
							/>
						</InlineDialog>
					</>
				)}
			</Field>
		);

		const handleTrigger = (triggerProps: TriggerProps) => (
			<span {...triggerProps} role="menu">
				{labelsField}
			</span>
		);

		return showColorPickerPopup &&
			expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			) ? (
			<Popup
				zIndex={themeLayers.modal()}
				isOpen={showColorPickerPopup}
				autoFocus={false}
				placement="bottom-start"
				fallbackPlacements={['bottom-end']}
				trigger={handleTrigger}
				popupComponent={PopupContainer}
				onClose={this.resetLabelsColorPickerState}
				content={popupContent}
				messageId="label-picker.popup.jira-label-picker.ui.pickers.label.popup"
				messageType="transactional"
			/>
		) : (
			labelsField
		);
	}
}

type ExtendedPopupComponentProps = PopupComponentProps & {
	onClick?: (e: React.MouseEvent) => void;
};
const PopupContainer = forwardRef<HTMLDivElement, ExtendedPopupComponentProps>((props, ref) => (
	<div {...props} ref={ref} />
));

export default injectIntl(LabelsPicker);
